import { DOC_BASE_URL, toastError } from "./constants";

// A deliverable / sub deliverable carries at most two attachments at any moment.
// A third one is only possible after removing one of the two
export const MAX_ATTACHMENTS = 2;

// What the API keys off - 1 stores a newly picked file, 2 removes a stored one
export const ATTACHMENT_ACTION_ADD = 1;
export const ATTACHMENT_ACTION_REMOVE = 2;

const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
const MAX_FILE_SIZE = 5242880; // 5 MB

const convertToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const getExtension = (name) =>
  String(name || "").split(".").pop().toLowerCase();

// Returns the picked file as base64 along with its name and type, or null when
// the file is rejected - the caller only has to clear the input in that case
export const pickAttachmentFile = async (file) => {
  if (!file) return null;

  const extension = getExtension(file.name);

  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    toastError("Only .pdf, .jpg, .jpeg and .png files are allowed");
    return null;
  }

  if (file.size > MAX_FILE_SIZE) {
    toastError("File size more than 5 MB is not allowed");
    return null;
  }

  const base64 = await convertToBase64(file);

  return {
    AttachmentFilePath: base64,
    FileName: file.name,
    FileType: extension,
  };
};

// A stored attachment is a path under the document folder, one that was just
// picked is still a data url and opens as it is
export const getAttachmentUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("data:")) return path;
  return `${DOC_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Two files of the same name can sit on the same deliverable, so the upload
// stamps the time onto the stored name - the user should still see what they
// picked, not the stamp
const stripUploadStamp = (name) => String(name || "").replace(/^\d{13}_/, "");

// The stored path ends with the file name, a freshly picked file carries it
export const getAttachmentName = (attachment) => {
  if (!attachment) return "";
  if (attachment.FileName) return stripUploadStamp(attachment.FileName);

  const path = attachment.AttachmentFilePath || "";
  if (!path || path.startsWith("data:")) return "Attachment";

  return stripUploadStamp(path.split("/").pop());
};

// The attachment result set uses its own column names, so everything the screens
// read is filled in here. Who uploaded a file matters as much as the file does -
// both screens are worked by several people at once
export const normalizeAttachmentRow = (row) => ({
  ExhPlanAttachmentDtlID: row.ExhPlanAttachmentDtlID ?? null,
  ExhVendorPlanDtlID: row.ExhVendorPlanDtlID ?? null,
  ExhVendorPointDtlID: row.ExhVendorPointDtlID ?? null,
  // Daily routine rows share ExhVendorPlanDtlID 0, this is what identifies them
  DailyRoutineHallChecklistMappingID:
    row.DailyRoutineHallChecklistMappingID ?? null,
  AttachmentFilePath: row.AttachmentFilePath ?? row.FilePath ?? null,
  FileName: row.FileName ?? null,
  FileType: row.FileType ?? getExtension(row.AttachmentFilePath ?? ""),
  UploadedByName: String(row.UploadedByName ?? "").trim(),
  UploadedBy: row.CreatedBy ?? null,
  UploadedDate: row.UploadedDate ?? row.CreatedDate ?? null,
  isNew: false,
});

// Attachments arrive as their own result set, so pull whichever key of the
// response carries that list
export const extractAttachmentRows = (data) => {
  if (!data) return [];
  const candidates = [
    data.attachmentList,
    data.attachmentDetails,
    data.vendorPlanAttachments,
  ];
  const list = candidates.find((item) => Array.isArray(item)) || [];
  // Removed rows come back inactive, they must not take up one of the two slots
  return list.filter(
    (row) => row.IsActive == null || row.IsActive === true || row.IsActive === 1
  );
};

// A deliverable / sub deliverable shows two files and no more. The API already
// hands out two, this is what keeps the screens right if a plan stored before the
// cap existed still has a third active row
export const capAttachments = (list) =>
  (list || [])
    .slice()
    .sort(
      (a, b) =>
        (a.ExhPlanAttachmentDtlID ?? Number.MAX_SAFE_INTEGER) -
        (b.ExhPlanAttachmentDtlID ?? Number.MAX_SAFE_INTEGER)
    )
    .slice(0, MAX_ATTACHMENTS);
